import { ChevronDownIcon, ChevronRight } from 'lucide-react'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { paths } from 'src/config/paths'
import { Iconmark } from 'src/shared/components/icons/logos/iconmark'
import { Button } from 'src/shared/components/ui/buttons/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'src/shared/components/ui/popups/dropdown-menu'
import { useAuthState } from 'src/shared/features/auth/api/use-auth-state'
import { useSignOut } from 'src/shared/features/auth/api/use-signout'
import AuthMainDialog from 'src/shared/features/auth/components/auth-main-dialog'
import { ProfilePicIcon } from 'src/shared/features/home/assets/pfp'
import { useWindowSize } from 'src/shared/hooks/use-window-size'
import { cn } from 'src/shared/utils/cn'

import { useMyUserData } from '../../api/accounts/use-my-user'

import { LogoutIcon } from './icons/logout'

const Container = ({
  children,
  showModal,
  setShowModal,
  initialView,
}: {
  children: React.ReactNode
  showModal: boolean
  setShowModal: (showModal: boolean) => void
  initialView: 'login' | 'signup'
}) => {
  return (
    <>
      <div className="flex items-center gap-3">
        {children}
        {/* <AuthModal flow={initialView} open={showModal} onClose={() => setShowModal(false)} /> */}
        <AuthMainDialog
          open={showModal}
          setOpen={setShowModal}
          initialView={initialView === 'signup' ? 'consumer-signup' : 'consumer-login'}
        />
      </div>
    </>
  )
}

interface HeaderAuthIndicatorProps {
  light?: boolean
  theme?: 'dark' | 'light'
  variant?: 'marketplace' | 'home' | 'public-profile' | 'enterprise'
  buttonClassName?: string
  loginButtonText?: string
  /** Override the default login button click behavior */
  loginButtonOnClick?: () => void
}

const HeaderAuthIndicator = ({
  light = false,
  theme = 'dark',
  buttonClassName,
  variant = 'home',
  loginButtonText = 'Login',
  loginButtonOnClick,
}: HeaderAuthIndicatorProps) => {
  const navigate = useNavigate()
  const { data: user } = useMyUserData()
  const { isSignedIn: isUserSignedIn, isConsumerSignedIn: isConsumerSignedIn } = useAuthState()
  const signOut = useSignOut({ isConsumer: true })
  const [initialView, setInitialView] = useState<'login' | 'signup'>('login')
  const [showModal, setShowModal] = useState(false)
  const { width } = useWindowSize()
  const isMobile = width < 768

  const textColorClass = light ? 'text-white' : 'text-primary'

  const handleClientAuthClick = (signup = false) => {
    if (loginButtonOnClick) {
      loginButtonOnClick()
      return
    }

    if (isMobile) {
      navigate(paths.consumer.auth.login.getHref())
    } else {
      setInitialView(signup ? 'signup' : 'login')
      setShowModal(true)
    }
  }

  const handleLogout = async () => {
    await signOut.mutateAsync()
    if (variant === 'enterprise') {
      navigate(paths.enterprise.getHref())
    } else {
      navigate(paths.root.getHref())
    }
  }

  const handleGoToClientPortal = () => {
    navigate(paths.client.index.path)
  }

  const handleGoToEnterpriseDashboard = () => {
    navigate(paths.enterprise.dashboard.getHref())
  }

  const handleGoToNetwork = () => {
    navigate(paths.business.getHref())
  }

  if (variant === 'enterprise') {
    return (
      <>
        <Container showModal={showModal} setShowModal={setShowModal} initialView={initialView}>
          {isConsumerSignedIn || isUserSignedIn ? (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="default"
                  size="sm"
                  className={cn(
                    'h-[30px] min-h-0 flex-shrink-0 items-center gap-1 rounded-full bg-primary pl-3 pr-2 text-[14px] font-medium text-white xs:h-[35px] xs:pl-4 xs:pr-3',
                    theme === 'light' && 'bg-white text-primary',
                    isMobile && 'text-[12px]',
                    buttonClassName,
                  )}
                >
                  <p className="font-medium">Account</p>
                  <ChevronDownIcon className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                sideOffset={4}
                align="end"
                className="mt-1 w-[200px] flex-col items-start justify-start gap-2 rounded-[20px] border-border p-3 xs:w-[220px] xs:gap-4 xs:p-4"
              >
                <DropdownMenuItem className="group mb-3 ml-2 flex size-full cursor-pointer items-center justify-between gap-2 p-0 text-left hover:bg-white focus:bg-white xs:mb-3 xs:gap-3">
                  <button
                    type="button"
                    onClick={isConsumerSignedIn ? handleGoToClientPortal : handleGoToEnterpriseDashboard}
                    className="flex flex-row items-center justify-between gap-3"
                  >
                    <Iconmark className={`min-h-5 min-w-5 text-primary`} />
                    <p className={`text-[14px] font-semibold text-primary xs:text-base`}>
                      {isConsumerSignedIn ? 'Client Portal' : 'Dashboard'}
                    </p>
                  </button>
                  <ChevronRight
                    className={`min-h-6 min-w-6 text-primary opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus:opacity-100`}
                  />
                </DropdownMenuItem>
                <DropdownMenuItem className="group flex size-full cursor-pointer items-center justify-between gap-3 p-0 text-left hover:bg-white focus:bg-white">
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="flex flex-row items-center justify-between gap-3"
                  >
                    <LogoutIcon className={`ml-2 min-h-5 min-w-5 text-primary`} />
                    <p className={`text-[14px] font-semibold text-primary xs:text-base`}>Log out</p>
                  </button>
                  <ChevronRight
                    className={`min-h-6 min-w-6 text-primary opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus:opacity-100`}
                  />
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              onClick={() => handleClientAuthClick(false)}
              variant="default"
              size="sm"
              className={cn(
                'h-[30px] min-h-0 flex-shrink-0 gap-3 rounded-full bg-primary px-3 text-[14px] text-white xs:h-[35px] xs:px-4',
                theme === 'light' && 'bg-white text-primary',
                isMobile && 'text-[12px]',
                buttonClassName,
              )}
            >
              {/* <ProfilePicIcon className="size-6 text-white" /> */}
              <p className="font-medium">{loginButtonText}</p>
            </Button>
          )}
        </Container>
      </>
    )
  }

  if (variant === 'home') {
    return (
      <>
        <Container showModal={showModal} setShowModal={setShowModal} initialView={initialView}>
          {isConsumerSignedIn || isUserSignedIn ? (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="default"
                  size="sm"
                  className={cn(
                    'h-[30px] min-h-0 flex-shrink-0 items-center gap-1 rounded-full bg-primary pl-3 pr-2 text-[14px] font-medium text-white xs:h-[35px] xs:pl-4 xs:pr-3',
                    theme === 'light' && 'bg-white text-primary',
                    isMobile && 'text-[12px]',
                    buttonClassName,
                  )}
                >
                  <p className="font-medium">Account</p>
                  <ChevronDownIcon className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                sideOffset={4}
                align="end"
                className="mt-1 w-[200px] flex-col items-start justify-start gap-2 rounded-[20px] border-border p-3 xs:w-[220px] xs:gap-4 xs:p-4"
              >
                <DropdownMenuItem className="group mb-3 ml-2 flex size-full cursor-pointer items-center justify-between gap-2 p-0 text-left hover:bg-white focus:bg-white xs:mb-3 xs:gap-3">
                  <button
                    type="button"
                    onClick={isConsumerSignedIn ? handleGoToClientPortal : handleGoToNetwork}
                    className="flex flex-row items-center justify-between gap-3"
                  >
                    <Iconmark className={`min-h-5 min-w-5 text-primary`} />
                    <p className={`text-[14px] font-semibold text-primary xs:text-base`}>
                      {isConsumerSignedIn ? 'Client Portal' : 'Go to Network'}
                    </p>
                  </button>
                  <ChevronRight
                    className={`min-h-6 min-w-6 text-primary opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus:opacity-100`}
                  />
                </DropdownMenuItem>
                <DropdownMenuItem className="group flex size-full cursor-pointer items-center justify-between gap-3 p-0 text-left hover:bg-white focus:bg-white">
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="flex flex-row items-center justify-between gap-3"
                  >
                    <LogoutIcon className={`ml-2 min-h-5 min-w-5 text-primary`} />
                    <p className={`text-[14px] font-semibold text-primary xs:text-base`}>Log out</p>
                  </button>
                  <ChevronRight
                    className={`min-h-6 min-w-6 text-primary opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus:opacity-100`}
                  />
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              onClick={() => handleClientAuthClick(false)}
              variant="default"
              size="sm"
              className={cn(
                'h-[30px] min-h-0 flex-shrink-0 gap-3 rounded-full bg-primary px-3 text-[14px] text-white xs:h-[35px] xs:px-4',
                theme === 'light' && 'bg-white text-primary',
                isMobile && 'text-[12px]',
                buttonClassName,
              )}
            >
              {/* <ProfilePicIcon className="size-6 text-white" /> */}
              <p className="font-medium">{loginButtonText}</p>
            </Button>
          )}
        </Container>
      </>
    )
  }

  if (isMobile && variant !== 'public-profile') {
    if (isConsumerSignedIn) {
      return (
        <Container showModal={showModal} setShowModal={setShowModal} initialView={initialView}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                // onClick={() => navigate(paths.users.profile.getHref(user.username))}
                className={cn(
                  'flex min-h-[50px] w-max cursor-pointer flex-row items-center gap-0.5 py-2 text-base',
                  buttonClassName,
                )}
              >
                <ProfilePicIcon className={`size-6 ${textColorClass}`} />
                <ChevronDownIcon className={`size-4 ${textColorClass}`} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              sideOffset={4}
              align="end"
              className="w-[220px] flex-col items-start justify-start gap-4 rounded-[20px] border-border p-4"
            >
              <DropdownMenuItem className="group mb-3 ml-2 flex size-full cursor-pointer items-center justify-between gap-3 p-0 text-left hover:bg-white focus:bg-white">
                <button
                  type="button"
                  onClick={handleGoToClientPortal}
                  className="flex flex-row items-center justify-between gap-3"
                >
                  <Iconmark className={`min-h-5 min-w-5 text-primary`} />
                  <p className={`text-base font-semibold text-primary`}>Client Portal</p>
                </button>
                <ChevronRight
                  className={`min-h-6 min-w-6 text-primary opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus:opacity-100`}
                />
              </DropdownMenuItem>
              <DropdownMenuItem className="group flex size-full cursor-pointer items-center justify-between gap-3 p-0 text-left hover:bg-white focus:bg-white">
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex flex-row items-center justify-between gap-3"
                >
                  <LogoutIcon className={`ml-2 min-h-5 min-w-5 text-primary`} />
                  <p className={`text-base font-semibold text-primary`}>Log out</p>
                </button>
                <ChevronRight
                  className={`min-h-6 min-w-6 text-primary opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus:opacity-100`}
                />
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </Container>
      )
    }

    if (isUserSignedIn) {
      return (
        <Container showModal={showModal} setShowModal={setShowModal} initialView={initialView}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                // onClick={() => navigate(paths.users.profile.getHref(user.username))}
                className={cn(
                  'flex min-h-[50px] w-max cursor-pointer flex-row items-center gap-0.5 py-2 text-base',
                  buttonClassName,
                )}
              >
                {user?.profilePhoto ? (
                  <div
                    style={{
                      boxShadow: '0px 1px 6px 1px rgba(27, 48, 41, 0.10)',
                    }}
                    className="rounded-full border-2 border-white bg-white"
                  >
                    <img src={user?.profilePhoto} alt="Profile" className="size-[35px] rounded-full" />
                  </div>
                ) : (
                  <ProfilePicIcon className={`size-6 ${textColorClass}`} />
                )}
                <ChevronDownIcon className={`size-4 ${textColorClass}`} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              sideOffset={4}
              align="end"
              className="flex w-[220px] flex-col items-start justify-start gap-4 rounded-[20px] border-border p-4"
            >
              <DropdownMenuItem className="group flex size-full cursor-pointer items-center justify-between gap-3 p-0 text-left hover:bg-white focus:bg-white">
                <button
                  type="button"
                  onClick={handleGoToNetwork}
                  className="flex flex-row items-center justify-between gap-3"
                >
                  <Iconmark className={`min-h-5 min-w-5 text-primary`} />
                  <p className={`text-base font-semibold text-primary`}>Go to Network</p>
                </button>
                <ChevronRight
                  className={`min-h-6 min-w-6 text-primary opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus:opacity-100`}
                />
              </DropdownMenuItem>
              <DropdownMenuItem className="group flex size-full cursor-pointer items-center justify-between gap-3 p-0 text-left hover:bg-white focus:bg-white">
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex flex-row items-center justify-between gap-3"
                >
                  <LogoutIcon className={`min-h-5 min-w-5 text-primary`} />
                  <p className={`text-base font-semibold text-primary`}>Log out</p>
                </button>
                <ChevronRight
                  className={`min-h-6 min-w-6 text-primary opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus:opacity-100`}
                />
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </Container>
      )
    }

    return (
      <>
        <Container showModal={showModal} setShowModal={setShowModal} initialView={initialView}>
          <button
            onClick={() => handleClientAuthClick(false)}
            className={cn(
              'flex min-h-[50px] w-max cursor-pointer flex-row items-center py-2 text-base',
              buttonClassName,
            )}
          >
            <ProfilePicIcon className={`size-6 ${textColorClass}`} />
          </button>
        </Container>
      </>
    )
  }

  if (isConsumerSignedIn) {
    return (
      <Container showModal={showModal} setShowModal={setShowModal} initialView={initialView}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              // onClick={() => navigate(paths.users.profile.getHref(user.username))}
              className={cn(
                'flex min-h-[50px] w-max cursor-pointer flex-row items-center gap-2 rounded-full border border-[#d8d8d6] bg-accent px-5 py-2 text-base',
                buttonClassName,
              )}
            >
              <ProfilePicIcon className={`size-6 ${textColorClass}`} />
              <div className="flex flex-row items-center justify-center gap-1">
                <p className={`flex size-full items-center justify-center text-base font-semibold ${textColorClass}`}>
                  Account
                </p>
                <ChevronDownIcon className={`min-h-5 min-w-5 ${textColorClass}`} />
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            sideOffset={4}
            align="end"
            className="w-[220px] flex-col items-start justify-start gap-4 rounded-[20px] border-border p-4"
          >
            <DropdownMenuItem className="group mb-3 ml-2 flex size-full cursor-pointer items-center justify-between gap-3 p-0 text-left hover:bg-white focus:bg-white">
              <button
                type="button"
                onClick={handleGoToClientPortal}
                className="flex flex-row items-center justify-between gap-3"
              >
                <Iconmark className={`min-h-5 min-w-5 text-primary`} />
                <p className={`text-base font-semibold text-primary`}>Client Portal</p>
              </button>
              <ChevronRight
                className={`min-h-6 min-w-6 text-primary opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus:opacity-100`}
              />
            </DropdownMenuItem>
            <DropdownMenuItem className="group flex size-full cursor-pointer items-center justify-between gap-3 p-0 text-left hover:bg-white focus:bg-white">
              <button type="button" onClick={handleLogout} className="flex flex-row items-center justify-between gap-3">
                <LogoutIcon className={`ml-2 min-h-5 min-w-5 text-primary`} />
                <p className={`text-base font-semibold text-primary`}>Log out</p>
              </button>
              <ChevronRight
                className={`min-h-6 min-w-6 text-primary opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus:opacity-100`}
              />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </Container>
    )
  }

  if (isUserSignedIn) {
    return (
      <Container showModal={showModal} setShowModal={setShowModal} initialView={initialView}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              // onClick={() => navigate(paths.users.profile.getHref(user.username))}
              className={cn(
                'flex min-h-[50px] w-max cursor-pointer flex-row items-center gap-2 rounded-full border border-[#d8d8d6] bg-accent px-5 py-2 text-base',
                buttonClassName,
              )}
            >
              {user?.profilePhoto ? (
                <img
                  src={user?.profilePhoto}
                  alt="Profile"
                  className="size-[35px] rounded-full border-2 border-white"
                  style={{
                    boxShadow: '0px 1px 6px 1px rgba(27, 48, 41, 0.10)',
                  }}
                />
              ) : (
                <ProfilePicIcon className={`size-6 ${textColorClass}`} />
              )}
              <div className="flex flex-row items-center justify-center gap-1">
                <p className={`flex size-full items-center justify-center text-base font-semibold ${textColorClass}`}>
                  Account
                </p>
                <ChevronDownIcon className={`min-h-5 min-w-5 ${textColorClass}`} />
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            sideOffset={4}
            align="end"
            className="flex w-[220px] flex-col items-start justify-start gap-4 rounded-[20px] border-border p-4"
          >
            <DropdownMenuItem className="group flex size-full cursor-pointer items-center justify-between gap-3 p-0 text-left hover:bg-white focus:bg-white">
              <button
                type="button"
                onClick={handleGoToNetwork}
                className="flex flex-row items-center justify-between gap-3"
              >
                <Iconmark className={`min-h-5 min-w-5 text-primary`} />
                <p className={`text-base font-semibold text-primary`}>Go to Network</p>
              </button>
              <ChevronRight
                className={`min-h-6 min-w-6 text-primary opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus:opacity-100`}
              />
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleLogout}
              className="group flex size-full cursor-pointer items-center justify-between gap-3 p-0 text-left hover:bg-white focus:bg-white"
            >
              <button type="button" className="flex flex-row items-center justify-between gap-3">
                <LogoutIcon className={`min-h-5 min-w-5 text-primary`} />
                <p className={`text-base font-semibold text-primary`}>Log out</p>
              </button>
              <ChevronRight
                className={`min-h-6 min-w-6 text-primary opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus:opacity-100`}
              />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </Container>
    )
  }

  return (
    <>
      <Container showModal={showModal} setShowModal={setShowModal} initialView={initialView}>
        <Button
          onClick={() => handleClientAuthClick(false)}
          variant="default"
          className={cn(
            'w-max gap-3 rounded-full px-4 text-[13px] md:min-h-[50px] md:px-7 md:text-base',
            buttonClassName,
          )}
        >
          <ProfilePicIcon className="size-6 text-white" />
          <p className="font-semibold text-white">Sign in</p>
        </Button>
      </Container>
    </>
  )
}

export default HeaderAuthIndicator
